import { useMemo, useState } from 'react';
import GlassCard from '../components/ui/GlassCard';
import GlassSelect from '../components/ui/GlassSelect';
import GlassInput from '../components/ui/GlassInput';
import GlassFileUpload from '../components/ui/GlassFileUpload';
import Button from '../components/ui/Button';
import Field from '../components/ui/Field';
import StatusPill from '../components/ui/StatusPill';
import Modal from '../components/ui/Modal';
import PageHeader from '../components/layout/PageHeader';
import {
  usePresupuestos,
  useTipoConceptos,
  useConceptos,
} from '../hooks/useCatalogs';
import {
  FIELD_NAMES,
  REQUIRED_FIELDS,
  validateTransaction,
} from '../utils/transactionSchema';
import { formatCurrency, todayIsoDate, formatDate } from '../utils/formatters';
import { api } from '../services/api';
import { fileToBase64 } from '../utils/fileToBase64';

const INITIAL_VALUES = {
  [FIELD_NAMES.presupuesto]: '',
  [FIELD_NAMES.tipoConcepto]: '',
  [FIELD_NAMES.concepto]: '',
  [FIELD_NAMES.fecha]: todayIsoDate(),
  [FIELD_NAMES.monto]: '',
  [FIELD_NAMES.detalle]: '',
  [FIELD_NAMES.comprobante]: null,
};

const SparkIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" aria-hidden="true" className="h-3.5 w-3.5">
    <path
      d="M12 2v6M12 16v6M2 12h6M16 12h6M5 5l4 4M15 15l4 4M19 5l-4 4M9 15l-4 4"
      stroke="currentColor"
      strokeWidth="1.6"
      strokeLinecap="round"
    />
  </svg>
);

const CalendarIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" aria-hidden="true" className="h-3.5 w-3.5">
    <rect
      x="3"
      y="5"
      width="18"
      height="16"
      rx="2"
      stroke="currentColor"
      strokeWidth="1.6"
    />
    <path
      d="M3 9h18M8 3v4M16 3v4"
      stroke="currentColor"
      strokeWidth="1.6"
      strokeLinecap="round"
    />
  </svg>
);

function Transactions() {
  const {
    items: presupuestos,
    isLoading: isLoadingPresupuestos,
    error: presupuestosError,
  } = usePresupuestos();
  const {
    items: tipoConceptos,
    isLoading: isLoadingTipos,
    error: tiposError,
  } = useTipoConceptos();
  const {
    items: conceptos,
    isLoading: isLoadingConceptos,
    error: conceptosError,
  } = useConceptos();

  const [values, setValues] = useState(INITIAL_VALUES);
  const [errors, setErrors] = useState({});
  const [touched, setTouched] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [feedback, setFeedback] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [submitError, setSubmitError] = useState(null);

  const filteredConceptos = useMemo(() => {
    if (!values[FIELD_NAMES.tipoConcepto]) return conceptos;
    return conceptos.filter((c) => c.type === values[FIELD_NAMES.tipoConcepto]);
  }, [conceptos, values]);

  const hasAnyCatalogError =
    Boolean(presupuestosError) ||
    Boolean(tiposError) ||
    Boolean(conceptosError);

  const handleChange = (name) => (event) => {
    const next = event.target.value;
    setValues((prev) => {
      const updated = { ...prev, [name]: next };
      if (name === FIELD_NAMES.tipoConcepto) {
        updated[FIELD_NAMES.concepto] = '';
      }
      return updated;
    });
    if (name === FIELD_NAMES.tipoConcepto) {
      setTouched((prev) => ({ ...prev, [FIELD_NAMES.concepto]: false }));
      setErrors((prev) => {
        if (!(FIELD_NAMES.concepto in prev)) return prev;
        const next = { ...prev };
        delete next[FIELD_NAMES.concepto];
        return next;
      });
    }
    if (touched[name] || errors[name]) {
      const nextErrors = validateTransaction({
        ...values,
        [name]: next,
      });
      setErrors(nextErrors);
    }
    setFeedback(null);
  };

  const handleMontoChange = (event) => {
    const raw = event.target.value;
    const sanitized = raw.replace(/[^\d.,]/g, '').replace(',', '.');
    setValues((prev) => ({ ...prev, [FIELD_NAMES.monto]: sanitized }));
    if (touched[FIELD_NAMES.monto] || errors[FIELD_NAMES.monto]) {
      const nextErrors = validateTransaction({
        ...values,
        [FIELD_NAMES.monto]: sanitized,
      });
      setErrors(nextErrors);
    }
    setFeedback(null);
  };

  const handleFileChange = (file) => {
    setValues((prev) => ({ ...prev, [FIELD_NAMES.comprobante]: file }));
    if (file && errors[FIELD_NAMES.comprobante]) {
      const nextErrors = validateTransaction({
        ...values,
        [FIELD_NAMES.comprobante]: file,
      });
      setErrors(nextErrors);
    }
    setFeedback(null);
  };

  const handleBlur = (name) => () => {
    setTouched((prev) => ({ ...prev, [name]: true }));
    setErrors(validateTransaction(values));
  };

  const handleReset = () => {
    setValues(INITIAL_VALUES);
    setErrors({});
    setTouched({});
    setFeedback(null);
  };

  const handleSubmit = (event) => {
    event.preventDefault();
    const nextErrors = validateTransaction(values);
    setErrors(nextErrors);
    setTouched(
      REQUIRED_FIELDS.reduce((acc, field) => {
        acc[field.name] = true;
        return acc;
      }, {}),
    );

    if (Object.keys(nextErrors).length > 0) {
      setFeedback({
        type: 'error',
        message: 'Revisa los campos marcados antes de continuar.',
      });
      return;
    }

    setFeedback(null);
    setSubmitError(null);
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    if (isSubmitting) return;
    setIsModalOpen(false);
    setSubmitError(null);
  };

  const handleConfirmTransaction = async () => {
    if (!values.comprobante) {
      setSubmitError('Adjunta un comprobante antes de confirmar.');
      return;
    }
    setIsSubmitting(true);
    setSubmitError(null);
    try {
      const { base64, format } = await fileToBase64(values.comprobante);
      const response = await api.postTransaccion({
        cd_presupuesto: Number(values.presupuesto),
        fe_presupuesto: values.fecha,
        cd_concepto: Number(values.concepto),
        mt_transanccion: Number(values.monto),
        de_transaccion: values.detalle,
        de_imagen: base64,
        formato_imagen: format,
      });
      if (response.ok) {
        setIsModalOpen(false);
        setSubmitError(null);
        handleReset();
        setFeedback({
          type: 'success',
          message: 'Transacción registrada con éxito.',
        });
      } else {
        let message = `No se pudo registrar (HTTP ${response.status}).`;
        try {
          const body = await response.json();
          if (body?.message) message = body.message;
        } catch {
          // respuesta sin JSON, se mantiene el mensaje genérico
        }
        setSubmitError(message);
      }
    } catch {
      setSubmitError('Error de conexión. Verifica tu red e intenta nuevamente.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const selectedTipo = tipoConceptos.find(
    (t) => t.value === values[FIELD_NAMES.tipoConcepto],
  );
  const selectedPresupuesto = presupuestos.find(
    (p) => p.value === values[FIELD_NAMES.presupuesto],
  );
  const selectedConcepto = conceptos.find(
    (c) => c.value === values[FIELD_NAMES.concepto],
  );

  return (
    <div className="relative mx-auto w-full max-w-6xl px-5 pb-24 pt-10 sm:px-8 sm:pt-16">
      <PageHeader onAddTransaction={handleReset} />

      {feedback && (
        <div
          role="status"
          className={`mt-8 flex items-center gap-3 rounded-2xl border px-4 py-3 text-sm animate-fade-up ${feedback.type === 'success' ? 'border-emerald-300/30 bg-emerald-300/[0.06] text-emerald-200' : 'border-rose-300/30 bg-rose-300/[0.06] text-rose-200'}`}
        >
          <span className="flex h-6 w-6 items-center justify-center rounded-full bg-white/10">
            <SparkIcon />
          </span>
          {feedback.message}
        </div>
      )}

      <div className="mt-10 grid gap-6 lg:grid-cols-[minmax(0,1fr)_minmax(0,1.2fr)]">
        <SummaryPanel
          selectedTipo={selectedTipo?.value}
          monto={values[FIELD_NAMES.monto]}
        />

        <GlassCard className="p-6 sm:p-8">
          <div className="mb-6 flex items-center justify-between gap-4">
            <div>
              <h2 className="font-serif text-2xl tracking-tight">
                <span className="italic text-gradient">Nueva transacción</span>
              </h2>
              <p className="mt-1 text-xs font-mono uppercase tracking-[0.18em] text-white/40">
                formulario · 7 campos
              </p>
            </div>
            <span className="hidden h-10 w-10 items-center justify-center rounded-2xl border border-white/10 bg-white/[0.04] sm:flex">
              <CalendarIcon />
            </span>
          </div>

          <form
            onSubmit={handleSubmit}
            noValidate
            className="grid grid-cols-1 gap-5 sm:grid-cols-2"
          >
            <Field
              label="Presupuesto disponible"
              required
              htmlFor="presupuesto"
              error={touched.presupuesto ? errors.presupuesto : undefined}
              hint="Origen del movimiento"
              className="sm:col-span-1"
            >
              <GlassSelect
                id="presupuesto"
                label="Presupuesto disponible"
                options={presupuestos}
                value={values.presupuesto}
                onChange={handleChange(FIELD_NAMES.presupuesto)}
                onBlur={handleBlur(FIELD_NAMES.presupuesto)}
                isLoading={isLoadingPresupuestos}
                isInvalid={Boolean(touched.presupuesto && errors.presupuesto)}
                placeholder="Selecciona un presupuesto"
                emptyMessage="No hay presupuestos disponibles"
              />
            </Field>

            <Field
              label="Tipo de concepto"
              required
              htmlFor="tipo-concepto"
              error={touched.tipoConcepto ? errors.tipoConcepto : undefined}
              hint="Ingreso, egreso o ahorro"
              badge={
                selectedTipo && (
                  <StatusPill type={selectedTipo.value} />
                )
              }
            >
              <GlassSelect
                id="tipo-concepto"
                label="Tipo de concepto"
                options={tipoConceptos}
                value={values.tipoConcepto}
                onChange={handleChange(FIELD_NAMES.tipoConcepto)}
                onBlur={handleBlur(FIELD_NAMES.tipoConcepto)}
                isLoading={isLoadingTipos}
                isInvalid={Boolean(touched.tipoConcepto && errors.tipoConcepto)}
                placeholder="Selecciona un tipo"
                emptyMessage="No hay tipos disponibles"
              />
            </Field>

            <Field
              label="Concepto"
              required
              htmlFor="concepto"
              error={touched.concepto ? errors.concepto : undefined}
              hint={
                values.tipoConcepto
                  ? 'Filtrado por el tipo seleccionado'
                  : 'Selecciona primero un tipo'
              }
              className="sm:col-span-2"
            >
              <GlassSelect
                id="concepto"
                label="Concepto"
                options={filteredConceptos}
                value={values.concepto}
                onChange={handleChange(FIELD_NAMES.concepto)}
                onBlur={handleBlur(FIELD_NAMES.concepto)}
                isLoading={isLoadingConceptos}
                isInvalid={Boolean(touched.concepto && errors.concepto)}
                disabled={!values.tipoConcepto}
                placeholder={
                  values.tipoConcepto
                    ? 'Selecciona un concepto'
                    : 'Selecciona un tipo primero'
                }
                emptyMessage="Sin conceptos para este tipo"
              />
            </Field>

            <Field
              label="Fecha de transacción"
              required
              htmlFor="fecha"
              error={touched.fecha ? errors.fecha : undefined}
            >
              <GlassInput
                id="fecha"
                label="Fecha de transacción"
                type="date"
                value={values.fecha}
                onChange={handleChange(FIELD_NAMES.fecha)}
                onBlur={handleBlur(FIELD_NAMES.fecha)}
                isInvalid={Boolean(touched.fecha && errors.fecha)}
              />
            </Field>

            <Field
              label="Monto"
              required
              htmlFor="monto"
              error={touched.monto ? errors.monto : undefined}
              hint="Monto en soles (PEN)"
            >
              <GlassInput
                id="monto"
                label="Monto"
                type="text"
                inputMode="decimal"
                value={values.monto}
                onChange={handleMontoChange}
                onBlur={handleBlur(FIELD_NAMES.monto)}
                isInvalid={Boolean(touched.monto && errors.monto)}
                placeholder="0.00"
                prefix="S/"
              />
            </Field>

            <Field
              label="Detalle de transacción"
              htmlFor="detalle"
              hint="Descripción o nota adicional (opcional)"
              className="sm:col-span-2"
            >
              <GlassInput
                id="detalle"
                label="Detalle de transacción"
                type="text"
                value={values.detalle}
                onChange={handleChange(FIELD_NAMES.detalle)}
                onBlur={handleBlur(FIELD_NAMES.detalle)}
                placeholder="Ej. Pago mensual de servicios, transferencia recibida, etc."
                maxLength={255}
              />
            </Field>

            <Field
              label="Comprobante"
              required
              htmlFor="comprobante"
              error={touched.comprobante ? errors.comprobante : undefined}
              hint="Imagen del comprobante"
              className="sm:col-span-2"
            >
              <GlassFileUpload
                id="comprobante"
                label="Comprobante"
                value={values.comprobante}
                onChange={handleFileChange}
                onBlur={handleBlur(FIELD_NAMES.comprobante)}
                isInvalid={Boolean(
                  touched.comprobante && errors.comprobante,
                )}
              />
            </Field>

            <div className="sm:col-span-2 mt-2 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
              <p className="text-xs text-white/40">
                {hasAnyCatalogError
                  ? 'No se pudieron cargar los catálogos. Reintenta más tarde.'
                  : 'Todos los campos son obligatorios.'}
              </p>
              <div className="flex flex-col gap-2 sm:flex-row sm:gap-3">
                <Button variant="secondary" type="button" onClick={handleReset} className="w-full sm:w-auto">
                  Limpiar
                </Button>
                <Button
                  type="submit"
                  icon={ArrowRightIcon}
                  className="w-full sm:w-auto"
                >
                  Registrar transacción
                </Button>
              </div>
            </div>
          </form>
        </GlassCard>
      </div>

      <footer className="mt-10 flex flex-col items-center justify-between gap-2 border-t border-white/5 pt-6 text-[11px] font-mono uppercase tracking-[0.2em] text-white/30 sm:flex-row">
        <span>Control de Gastos · FE</span>
        <span>v0.1.0 — build {new Date().getFullYear()}</span>
      </footer>

      <Modal
        isOpen={isModalOpen}
        onClose={handleCloseModal}
        title="Confirmar transacción"
        description="Revisa el resumen antes de registrar"
        footer={
          <>
            <Button
              variant="secondary"
              onClick={handleCloseModal}
              disabled={isSubmitting}
            >
              Cancelar
            </Button>
            <Button onClick={handleConfirmTransaction} isLoading={isSubmitting}>
              {isSubmitting ? 'Registrando…' : 'Confirmar'}
            </Button>
          </>
        }
      >
        <ConfirmationSummary
          items={[
            { label: 'Presupuesto', value: selectedPresupuesto?.label },
            { label: 'Tipo', value: selectedTipo?.label },
            { label: 'Concepto', value: selectedConcepto?.label },
            { label: 'Fecha', value: formatDate(values.fecha) },
            { label: 'Monto', value: formatCurrency(values.monto) },
            {
              label: 'Detalle',
              value: values.detalle ? values.detalle : '—',
              muted: !values.detalle,
            },
            { label: 'Comprobante', value: values.comprobante?.name },
          ]}
        />
        {submitError && (
          <div className="mt-4 flex items-start gap-3 rounded-2xl border border-rose-300/30 bg-rose-300/[0.06] px-4 py-3 text-sm text-rose-100 animate-fade-up">
            <span className="mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-rose-300/20 text-[11px] font-bold text-rose-200">
              !
            </span>
            <div className="space-y-0.5">
              <p className="font-medium text-rose-100">
                No se pudo registrar la transacción
              </p>
              <p className="text-xs text-rose-200/75">{submitError}</p>
            </div>
          </div>
        )}
      </Modal>
    </div>
  );
}

function ConfirmationSummary({ items }) {
  return (
    <dl className="divide-y divide-white/5 overflow-hidden rounded-2xl border border-white/10 bg-white/[0.03]">
      {items.map((item) => (
        <div
          key={item.label}
          className="flex items-center justify-between gap-4 px-4 py-3 sm:px-5"
        >
          <dt className="text-[11px] font-mono uppercase tracking-[0.18em] text-white/45">
            {item.label}
          </dt>
          <dd
            className={`text-right text-sm font-medium ${item.muted ? 'text-white/35' : 'text-white/90'}`}
          >
            {item.value || <span className="text-white/35">—</span>}
          </dd>
        </div>
      ))}
    </dl>
  );
}

function ArrowRightIcon() {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      aria-hidden="true"
      className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-0.5"
    >
      <path
        d="M5 12h14M13 6l6 6-6 6"
        stroke="currentColor"
        strokeWidth="1.8"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

function SummaryPanel({ selectedTipo, monto }) {
  const montoNumber = Number(monto);
  const formattedMonto = Number.isFinite(montoNumber) && montoNumber > 0
    ? formatCurrency(montoNumber)
    : 'S/ 0.00';

  const introMessage = selectedTipo
    ? 'Captura el monto, la fecha y el comprobante para cerrar el movimiento.'
    : 'Comienza eligiendo el tipo de movimiento que vas a registrar.';

  return (
    <GlassCard className="flex flex-col gap-8 p-6 sm:p-8">
      <div className="space-y-4">
        <span className="inline-flex items-center gap-2 rounded-full border border-white/10 bg-white/[0.04] px-3 py-1 text-[11px] font-mono uppercase tracking-[0.2em] text-white/55">
          <SparkIcon />
          Resumen
        </span>
        <p className="text-[15px] leading-relaxed text-white/70">
          {introMessage}
        </p>
      </div>

      <div className="rounded-2xl border border-white/10 bg-gradient-to-br from-white/[0.04] to-transparent p-5">
        <p className="text-[11px] font-mono uppercase tracking-[0.2em] text-white/45">
          Monto capturado
        </p>
        <p className="mt-2 font-mono text-3xl tracking-tight text-white sm:text-4xl">
          {formattedMonto}
        </p>
        <div className="mt-5 h-1.5 w-full overflow-hidden rounded-full bg-white/5">
          <div
            className="h-full rounded-full bg-gradient-to-r from-violet-400 via-fuchsia-400 to-cyan-300 transition-all duration-500"
            style={{
              width: `${Math.min(100, Math.max(8, (Number(monto) || 0) * 0.5))}%`,
            }}
          />
        </div>
        <p className="mt-3 text-xs text-white/45">
          Visualización referencial del registro.
        </p>
      </div>

      <div className="grid grid-cols-3 gap-3">
        {[
          { type: 'I', label: 'Ingreso' },
          { type: 'E', label: 'Egreso' },
          { type: 'A', label: 'Ahorro' },
        ].map(({ type, label }) => {
          const isActive = selectedTipo === type;
          return (
            <div
              key={type}
              className={`flex flex-col items-center gap-2 rounded-2xl border p-3 text-center transition ${isActive ? 'border-violet-300/40 bg-violet-300/[0.06]' : 'border-white/[0.06] bg-white/[0.02]'}`}
            >
              <StatusPill type={type} />
              <span className="text-[11px] font-mono uppercase tracking-wider text-white/55">
                {label}
              </span>
            </div>
          );
        })}
      </div>

      <div className="space-y-3 border-t border-white/5 pt-5 text-xs text-white/45">
        <p className="font-mono uppercase tracking-[0.2em] text-white/35">
          Atajos
        </p>
        <ul className="space-y-2">
          <li className="flex items-center gap-2">
            <span className="flex h-5 min-w-5 items-center justify-center rounded-md border border-white/10 bg-white/[0.04] px-1.5 font-mono text-[10px] text-white/55">
              ⌘
            </span>
            <span className="font-mono text-[11px] uppercase tracking-wider">K</span>
            Enfocar el campo activo
          </li>
          <li className="flex items-center gap-2">
            <span className="flex h-5 min-w-5 items-center justify-center rounded-md border border-white/10 bg-white/[0.04] px-1.5 font-mono text-[10px] text-white/55">
              Esc
            </span>
            <span className="font-mono text-[11px] uppercase tracking-wider">
              Limpiar selección
            </span>
          </li>
        </ul>
      </div>
    </GlassCard>
  );
}

export default Transactions;